import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  input,
  output,
  viewChild,
} from '@angular/core'

import { FilterParams } from '../shared/utilities/http-params'
import { IdrColumnSpec } from './idr-columns'
import { FormsModule } from '@angular/forms'
import { IdrMultiselectFilterComponent } from './idr-multiselect-filter.component'
import { IdrNumberRangeFilterComponent } from './idr-number-range-filter.component'
import { IdrDateRangeFilterComponent } from './idr-date-range-filter.component'

export const acquireFocusDelay = 100

type FocusableElement = HTMLSelectElement | HTMLInputElement

@Component({
  selector: 'app-idr-filter-value',
  imports: [FormsModule, IdrMultiselectFilterComponent, IdrNumberRangeFilterComponent, IdrDateRangeFilterComponent],
  template: `
    <div class="value-container">
      @if (dataType() === 'date' || dataType() === 'timestamp') {
        <app-idr-date-range
            [row]="row()"
            [columnSpec]="columnSpec()"
            [grabFocus]="grabFocus()"
            (rowUpdate)="rowUpdate.emit($event)"
        />
      } @else if (dataType() === 'boolean') {
        <select #select
            class='form-select'
            (change)="onUpdate(getEventValue($event))"
        >
          <option value='' [selected]="value() === ''">(select value)</option>
          <option value='true' [selected]="value() === 'true'">Yes</option>
          <option value='false' [selected]="value() === 'false'">No</option>
        </select>
      } @else if (dataType() === 'decimal') {
        <app-idr-number-range
            [row]="row()"
            [grabFocus]="grabFocus()"
            (rowUpdate)="rowUpdate.emit($event)"
        />
      } @else if (values()) {
        <app-idr-multiselect
            [value]="value()"
            [options]="values()!"
            [grabFocus]="grabFocus()"
            (update)="onUpdate($event)"
        >
        </app-idr-multiselect>
      } @else {
        <input
            #input
            type='text'
            class='form-control value-input'
            [value]="value()"
            (change)="onUpdate(getEventValue($event))"
        />
      }
    </div>
  `,
  styles: `
    .value-container {
      width: 100%;
      input {
        margin-top: 0;
        font-size: 16px;
      }
      .value-input {
        padding-top: 6px;
        padding-bottom: 6px;
      }
      select {
        width: 100%;
      }
    }
  `,
})
export class IdrFilterValueComponent implements AfterViewInit {
  readonly row = input.required<FilterParams>()
  readonly columnSpec = input.required<IdrColumnSpec>()
  readonly grabFocus = input.required<boolean>()
  readonly values = input<readonly string[]>()
  readonly rowUpdate = output<FilterParams>()

  protected readonly dataType = computed(() => this.columnSpec().dataType)
  protected readonly operator = computed(() => this.row().values[0].operation)
  protected readonly value = computed(() => this.row().values[0].value)

  private selectRef = viewChild<ElementRef<HTMLSelectElement>>('select')
  private inputRef = viewChild<ElementRef<HTMLInputElement>>('input')

  protected getEventValue({ target }: Event): string {
    return (target as HTMLInputElement | HTMLSelectElement).value
  }

  protected onUpdate(newValue: string): void {
    this.rowUpdate.emit({
      ...this.row(),
      values: [{
        ...this.row().values[0],
        value: newValue,
      }],
    })
  }

  private getFocusRef(): ElementRef<FocusableElement> | undefined {
    return this.selectRef() ?? this.inputRef()
  }

  ngAfterViewInit() {
    if (this.grabFocus() && this.getFocusRef()) {
      window.setTimeout(
        () => {
          this.getFocusRef()?.nativeElement.focus()
        },
        acquireFocusDelay,
      )
    }
  }
}
